import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney, formatDate, formatDateTime, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, Phone, FileText, Wallet, StickyNote, Download, Pencil } from "lucide-react";
import { API } from "@/lib/api";

const TABS = [
    { id: "invoices", label: "Faturalar", icon: FileText },
    { id: "collections", label: "Tahsilatlar", icon: Wallet },
    { id: "notes", label: "Görüşme Notları", icon: StickyNote },
];

export default function CustomerDetail() {
    const { id } = useParams();
    const [customer, setCustomer] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [collections, setCollections] = useState([]);
    const [notes, setNotes] = useState([]);
    const [tab, setTab] = useState("invoices");
    const [loading, setLoading] = useState(true);

    async function loadAll() {
        setLoading(true);
        try {
            const [c, inv, col, nts] = await Promise.all([
                api.get(`/customers/${id}`),
                api.get(`/customers/${id}/invoices`),
                api.get(`/customers/${id}/collections`),
                api.get(`/customers/${id}/notes`),
            ]);
            setCustomer(c.data);
            setInvoices(inv.data);
            setCollections(col.data);
            setNotes(nts.data);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadAll();
    }, [id]);

    if (loading) {
        return <div className="p-10 font-mono text-xs uppercase text-neutral-500">Yükleniyor…</div>;
    }
    if (!customer) return <div className="p-10">Müşteri bulunamadı.</div>;

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6">
                <Link
                    to="/musteriler"
                    className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-brand-ink mb-3"
                >
                    <ArrowLeft size={14} /> Müşteriler
                </Link>
                <div className="flex items-start justify-between gap-8">
                    <div>
                        <div className="overline">Cari Kart</div>
                        <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">
                            {customer.name}
                        </h1>
                        <div className="flex items-center gap-6 mt-4 text-xs font-mono text-neutral-600">
                            {customer.code && <span>Kod: {customer.code}</span>}
                            {customer.phone && (
                                <a href={`tel:${customer.phone}`} className="inline-flex items-center gap-1 hover:text-brand-blue">
                                    <Phone size={12} /> {customer.phone}
                                </a>
                            )}
                            {customer.email && <span>{customer.email}</span>}
                        </div>
                    </div>
                    <BalanceBlock customer={customer} />
                </div>
                <div className="mt-5 flex items-center gap-3">
                    <button
                        data-testid="download-statement"
                        onClick={async () => {
                            const token = localStorage.getItem("ferahis_token");
                            const res = await fetch(`${API}/customers/${id}/statement.xlsx`, {
                                headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) return toast.error("İndirme başarısız");
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `ekstre_${(customer.name || "musteri").replace(/\s+/g, "_")}.xlsx`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            URL.revokeObjectURL(url);
                        }}
                        className="btn-outline"
                    >
                        <Download size={13} /> Ekstre İndir (Excel)
                    </button>
                </div>
            </header>

            {/* Tabs */}
            <div className="px-10 border-b border-black/10 flex items-center gap-1">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        data-testid={`tab-${t.id}`}
                        onClick={() => setTab(t.id)}
                        className={`inline-flex items-center gap-2 px-5 py-4 text-xs font-mono uppercase tracking-widest border-b-2 -mb-px ${
                            tab === t.id
                                ? "border-brand-blue text-brand-ink"
                                : "border-transparent text-neutral-500 hover:text-brand-ink"
                        }`}
                    >
                        <t.icon size={14} /> {t.label}
                    </button>
                ))}
            </div>

            <div className="px-10 py-8">
                {tab === "invoices" && <InvoicesTab customerId={id} invoices={invoices} reload={loadAll} />}
                {tab === "collections" && <CollectionsTab customerId={id} collections={collections} reload={loadAll} />}
                {tab === "notes" && <NotesTab customerId={id} notes={notes} reload={loadAll} />}
                <AssignmentSection customer={customer} reload={loadAll} />
            </div>
        </div>
    );
}

function BalanceBlock({ customer }) {
    return (
        <div className="grid grid-cols-3 border border-black/10">
            <div className="p-4 border-r border-black/10 min-w-[130px]">
                <div className="overline">Fatura</div>
                <div className="font-mono-num text-lg mt-1">₺ {formatMoney(customer.invoiced)}</div>
            </div>
            <div className="p-4 border-r border-black/10 min-w-[130px]">
                <div className="overline">Tahsilat</div>
                <div className="font-mono-num text-lg mt-1 text-brand-green">₺ {formatMoney(customer.collected)}</div>
            </div>
            <div className="p-4 min-w-[130px] bg-neutral-50">
                <div className="overline">Bakiye</div>
                <div
                    data-testid="customer-balance"
                    className={`font-mono-num font-semibold text-xl mt-1 ${
                        customer.balance > 0 ? "text-brand-red" : customer.balance < 0 ? "text-brand-blue" : ""
                    }`}
                >
                    ₺ {formatMoney(customer.balance)}
                </div>
            </div>
        </div>
    );
}

// ------------ INVOICES ------------
function InvoicesTab({ customerId, invoices, reload }) {
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const empty = { invoice_no: "", amount: "", issue_date: new Date().toISOString().slice(0, 10), due_date: "", description: "" };
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    function openEdit(inv) {
        setEditId(inv.id);
        setForm({
            invoice_no: inv.invoice_no || "",
            amount: inv.amount,
            issue_date: inv.issue_date || "",
            due_date: inv.due_date || "",
            description: inv.description || "",
        });
        setOpen(true);
    }

    function openNew() {
        setEditId(null);
        setForm(empty);
        setOpen(true);
    }

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId,
                invoice_no: form.invoice_no,
                amount: Number(form.amount),
                issue_date: form.issue_date,
                due_date: form.due_date || null,
                description: form.description,
            };
            if (editId) {
                await api.put(`/invoices/${editId}`, payload);
                toast.success("Fatura güncellendi");
            } else {
                await api.post("/invoices", payload);
                toast.success("Fatura eklendi");
            }
            setOpen(false);
            setEditId(null);
            setForm(empty);
            reload();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Fatura silinsin mi?")) return;
        await api.delete(`/invoices/${id}`);
        toast.success("Silindi");
        reload();
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold tracking-tight">Faturalar</h2>
                <button onClick={openNew} data-testid="add-invoice-toggle" className="btn-sharp">
                    <Plus size={14} /> Yeni Fatura
                </button>
            </div>

            {open && (
                <form
                    data-testid="invoice-form"
                    onSubmit={submit}
                    className="grid grid-cols-2 md:grid-cols-5 gap-3 border border-black/15 p-5 mb-6 bg-neutral-50 animate-fade-up"
                >
                    <div className="md:col-span-5 overline">
                        {editId ? "Fatura Düzenle" : "Yeni Fatura"}
                    </div>
                    <F label="Fatura No" placeholder="Boş bırakılırsa otomatik (PT…) üretilir" value={form.invoice_no} onChange={(v) => setForm({ ...form, invoice_no: v })} testid="inv-no" />
                    <F label="Tutar (₺)" type="number" required value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="inv-amount" />
                    <F label="Fatura Tarihi" type="date" required value={form.issue_date} onChange={(v) => setForm({ ...form, issue_date: v })} testid="inv-issue" />
                    <F label="Vade" type="date" value={form.due_date || ""} onChange={(v) => setForm({ ...form, due_date: v })} testid="inv-due" />
                    <div className="md:col-span-5">
                        <F label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="inv-desc" />
                    </div>
                    <div className="md:col-span-5 flex justify-end gap-2">
                        <button type="button" onClick={() => { setOpen(false); setEditId(null); }} className="btn-outline">
                            Vazgeç
                        </button>
                        <button data-testid="inv-submit" className="btn-sharp" disabled={saving}>
                            {saving ? "Kaydediliyor…" : editId ? "Güncelle" : "Ekle"}
                        </button>
                    </div>
                </form>
            )}

            {invoices.length === 0 ? (
                <div className="text-sm text-neutral-500 font-mono">Fatura kaydı yok.</div>
            ) : (
                <table className="w-full text-sm" data-testid="invoices-table">
                    <thead>
                        <tr className="border-b border-black/10">
                            <th className="overline text-left py-3">Tarih</th>
                            <th className="overline text-left py-3">Fatura No</th>
                            <th className="overline text-left py-3">Vade</th>
                            <th className="overline text-left py-3">Durum</th>
                            <th className="overline text-right py-3">Tutar (₺)</th>
                            <th className="overline text-right py-3">Ödenen (₺)</th>
                            <th className="overline text-right py-3">Kalan (₺)</th>
                            <th className="w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map((i) => (
                            <tr key={i.id} className="border-b border-black/5">
                                <td className="py-3 font-mono text-xs">{formatDate(i.issue_date)}</td>
                                <td className="py-3">{i.invoice_no}</td>
                                <td className="py-3 font-mono text-xs">{formatDate(i.due_date)}</td>
                                <td className="py-3">
                                    <span
                                        data-testid={`inv-status-${i.id}`}
                                        className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                                            i.status === "paid"
                                                ? "text-brand-green border-brand-green"
                                                : i.status === "partial"
                                                  ? "text-brand-amber border-brand-amber"
                                                  : "text-brand-red border-brand-red"
                                        }`}
                                    >
                                        {i.status === "paid" ? "Ödendi" : i.status === "partial" ? "Kısmi" : "Ödenmedi"}
                                    </span>
                                </td>
                                <td className="py-3 text-right font-mono-num">{formatMoney(i.amount)}</td>
                                <td className="py-3 text-right font-mono-num text-brand-green">{formatMoney(i.paid || 0)}</td>
                                <td className="py-3 text-right font-mono-num text-brand-red">{formatMoney(i.remaining || 0)}</td>
                                <td className="py-3 text-right whitespace-nowrap">
                                    <button
                                        data-testid={`edit-invoice-${i.id}`}
                                        onClick={() => openEdit(i)}
                                        className="text-neutral-400 hover:text-brand-blue mr-3"
                                        title="Düzenle"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(i.id)} className="text-neutral-400 hover:text-brand-red" title="Sil">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ------------ COLLECTIONS ------------
const METHODS = {
    cash: "Nakit",
    bank_transfer: "Havale/EFT",
    check: "Çek",
    credit_card: "Kredi Kartı",
    other: "Diğer",
};

function CollectionsTab({ customerId, collections, reload }) {
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const empty = { amount: "", date: new Date().toISOString().slice(0, 10), method: "cash", reference: "", description: "", allocations: [] };
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [openInvoices, setOpenInvoices] = useState([]);

    useEffect(() => {
        api.get(`/customers/${customerId}/open-invoices`).then((r) => setOpenInvoices(r.data));
    }, [customerId, collections]);

    function openEdit(c) {
        setEditId(c.id);
        setForm({
            amount: c.amount,
            date: c.date || "",
            method: c.method || "cash",
            reference: c.reference || "",
            description: c.description || "",
            allocations: c.allocations || [],
        });
        setOpen(true);
    }

    function openNew() {
        setEditId(null);
        setForm(empty);
        setOpen(true);
    }

    function addAllocation() {
        setForm((f) => ({ ...f, allocations: [...(f.allocations || []), { invoice_id: "", amount: "" }] }));
    }
    function updateAllocation(idx, field, value) {
        setForm((f) => {
            const arr = [...(f.allocations || [])];
            arr[idx] = { ...arr[idx], [field]: value };
            return { ...f, allocations: arr };
        });
    }
    function removeAllocation(idx) {
        setForm((f) => ({ ...f, allocations: (f.allocations || []).filter((_, i) => i !== idx) }));
    }

    const allocSum = (form.allocations || []).reduce((s, a) => s + Number(a.amount || 0), 0);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                customer_id: customerId,
                amount: Number(form.amount),
                date: form.date,
                method: form.method,
                reference: form.reference,
                description: form.description,
                allocations: (form.allocations || [])
                    .filter((a) => a.invoice_id && Number(a.amount) > 0)
                    .map((a) => ({ invoice_id: a.invoice_id, amount: Number(a.amount) })),
            };
            if (editId) {
                await api.put(`/collections/${editId}`, payload);
                toast.success("Tahsilat güncellendi");
            } else {
                await api.post("/collections", payload);
                toast.success("Tahsilat kaydedildi");
            }
            setOpen(false);
            setEditId(null);
            setForm(empty);
            reload();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Tahsilat silinsin mi?")) return;
        await api.delete(`/collections/${id}`);
        toast.success("Silindi");
        reload();
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl font-bold tracking-tight">Tahsilatlar</h2>
                <button onClick={openNew} data-testid="add-collection-toggle" className="btn-sharp">
                    <Plus size={14} /> Yeni Tahsilat
                </button>
            </div>

            {open && (
                <form
                    data-testid="collection-form"
                    onSubmit={submit}
                    className="grid grid-cols-2 md:grid-cols-5 gap-3 border border-black/15 p-5 mb-6 bg-neutral-50 animate-fade-up"
                >
                    <div className="md:col-span-5 overline">
                        {editId ? "Tahsilat Düzenle" : "Yeni Tahsilat"}
                    </div>
                    <F label="Tutar (₺)" type="number" required value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="col-amount" />
                    <F label="Tarih" type="date" required value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="col-date" />
                    <label className="block">
                        <span className="overline block mb-2">Ödeme Şekli</span>
                        <select
                            data-testid="col-method"
                            value={form.method}
                            onChange={(e) => setForm({ ...form, method: e.target.value })}
                            className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue bg-white"
                        >
                            {Object.entries(METHODS).map(([k, v]) => (
                                <option key={k} value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </label>
                    <F label="Referans No" placeholder="Boş bırakılırsa otomatik (PT…) üretilir" value={form.reference} onChange={(v) => setForm({ ...form, reference: v })} testid="col-ref" />
                    <F label="Açıklama" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="col-desc" />

                    {/* Allocations */}
                    <div className="md:col-span-5 border-t border-black/10 pt-4 mt-2">
                        <div className="flex items-center justify-between mb-3">
                            <div className="overline">Fatura Eşleştirme (isteğe bağlı)</div>
                            <button type="button" onClick={addAllocation} data-testid="alloc-add" className="btn-outline">
                                <Plus size={12} /> Fatura Ekle
                            </button>
                        </div>
                        {(form.allocations || []).length === 0 && (
                            <div className="text-xs text-neutral-500 font-mono">
                                Bu tahsilatı hangi faturaya uyguladığınızı belirtmek için fatura ekleyebilirsiniz.
                            </div>
                        )}
                        {(form.allocations || []).map((a, idx) => (
                            <div key={idx} className="grid grid-cols-12 gap-2 mb-2" data-testid={`alloc-row-${idx}`}>
                                <select
                                    data-testid={`alloc-invoice-${idx}`}
                                    value={a.invoice_id}
                                    onChange={(e) => updateAllocation(idx, "invoice_id", e.target.value)}
                                    className="col-span-8 border border-black/20 px-2 py-1.5 text-xs font-mono bg-white focus:outline-none focus:border-brand-blue"
                                >
                                    <option value="">— Fatura seçin —</option>
                                    {openInvoices.map((inv) => (
                                        <option key={inv.id} value={inv.id}>
                                            {inv.invoice_no} · {formatDate(inv.issue_date)} · kalan ₺{formatMoney(inv.remaining)}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    data-testid={`alloc-amount-${idx}`}
                                    type="number"
                                    step="0.01"
                                    placeholder="Tutar"
                                    value={a.amount}
                                    onChange={(e) => updateAllocation(idx, "amount", e.target.value)}
                                    className="col-span-3 border border-black/20 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-brand-blue"
                                />
                                <button type="button" onClick={() => removeAllocation(idx)} className="col-span-1 text-neutral-400 hover:text-brand-red">
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        ))}
                        {(form.allocations || []).length > 0 && (
                            <div className="text-xs font-mono text-neutral-600 mt-2">
                                Dağıtılan toplam:{" "}
                                <span className="font-semibold">₺ {formatMoney(allocSum)}</span>{" "}
                                / Tahsilat: <span className="font-semibold">₺ {formatMoney(Number(form.amount) || 0)}</span>
                                {Math.abs(allocSum - Number(form.amount || 0)) > 0.01 && (
                                    <span className="text-brand-red ml-2">· Fark var</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div className="md:col-span-5 flex justify-end gap-2">
                        <button type="button" onClick={() => { setOpen(false); setEditId(null); }} className="btn-outline">
                            Vazgeç
                        </button>
                        <button data-testid="col-submit" className="btn-sharp" disabled={saving}>
                            {saving ? "Kaydediliyor…" : editId ? "Güncelle" : "Ekle"}
                        </button>
                    </div>
                </form>
            )}

            {collections.length === 0 ? (
                <div className="text-sm text-neutral-500 font-mono">Tahsilat kaydı yok.</div>
            ) : (
                <table className="w-full text-sm" data-testid="collections-table">
                    <thead>
                        <tr className="border-b border-black/10">
                            <th className="overline text-left py-3">Tarih</th>
                            <th className="overline text-left py-3">Yöntem</th>
                            <th className="overline text-left py-3">Referans</th>
                            <th className="overline text-left py-3">Tahsil Eden</th>
                            <th className="overline text-left py-3">Açıklama</th>
                            <th className="overline text-right py-3">Tutar (₺)</th>
                            <th className="w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {collections.map((c) => (
                            <tr key={c.id} className="border-b border-black/5">
                                <td className="py-3 font-mono text-xs">{formatDate(c.date)}</td>
                                <td className="py-3">{METHODS[c.method] || c.method}</td>
                                <td className="py-3 font-mono text-xs">{c.reference || "-"}</td>
                                <td className="py-3">{c.collector_name || "-"}</td>
                                <td className="py-3 text-neutral-600">{c.description || "-"}</td>
                                <td className="py-3 text-right font-mono-num text-brand-green">{formatMoney(c.amount)}</td>
                                <td className="py-3 text-right whitespace-nowrap">
                                    <button
                                        data-testid={`edit-collection-${c.id}`}
                                        onClick={() => openEdit(c)}
                                        className="text-neutral-400 hover:text-brand-blue mr-3"
                                        title="Düzenle"
                                    >
                                        <Pencil size={14} />
                                    </button>
                                    <button onClick={() => del(c.id)} className="text-neutral-400 hover:text-brand-red" title="Sil">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

// ------------ NOTES ------------
const OUTCOMES = {
    reached: "Ulaşıldı",
    no_answer: "Cevap Yok",
    promised_payment: "Ödeme Sözü",
    refused: "Reddedildi",
    voicemail: "Sesli Mesaj",
    other: "Diğer",
};
const OUTCOME_COLOR = {
    reached: "text-brand-green border-brand-green",
    no_answer: "text-neutral-500 border-neutral-400",
    promised_payment: "text-brand-blue border-brand-blue",
    refused: "text-brand-red border-brand-red",
    voicemail: "text-neutral-500 border-neutral-400",
    other: "text-neutral-500 border-neutral-400",
};

function NotesTab({ customerId, notes, reload }) {
    const [body, setBody] = useState("");
    const [outcome, setOutcome] = useState("reached");
    const [promised, setPromised] = useState("");
    const [promisedAmount, setPromisedAmount] = useState("");
    const [contactPerson, setContactPerson] = useState("");
    const [files, setFiles] = useState([]);
    const [saving, setSaving] = useState(false);

    async function submit(e) {
        e.preventDefault();
        if (!body.trim()) return;
        setSaving(true);
        try {
            const noteRes = await api.post("/notes", {
                customer_id: customerId,
                body,
                call_outcome: outcome,
                promised_date: outcome === "promised_payment" && promised ? promised : null,
                promised_amount: outcome === "promised_payment" && promisedAmount ? parseFloat(promisedAmount) : null,
                contact_person: contactPerson || "",
            });
            // upload attachments if any
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                try {
                    await api.post(`/notes/${noteRes.data.id}/attachments`, fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                } catch (err) {
                    toast.error(`${f.name}: ${formatApiError(err?.response?.data?.detail)}`);
                }
            }
            setBody("");
            setPromised("");
            setPromisedAmount("");
            setContactPerson("");
            setFiles([]);
            toast.success("Not eklendi");
            reload();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function del(id) {
        if (!window.confirm("Not silinsin mi?")) return;
        await api.delete(`/notes/${id}`);
        toast.success("Silindi");
        reload();
    }

    async function downloadAttachment(noteId, att) {
        const token = localStorage.getItem("ferahis_token");
        const res = await fetch(`${API}/notes/${noteId}/attachments/${att.id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return toast.error("İndirilemedi");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = att.filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            <div className="lg:col-span-2">
                <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Yeni Görüşme Notu</h2>
                <form onSubmit={submit} data-testid="note-form" className="border border-black/15 p-5 bg-neutral-50 space-y-4">
                    <label className="block">
                        <span className="overline block mb-2">Görüşülen Kişi</span>
                        <input
                            data-testid="note-contact-person"
                            value={contactPerson}
                            onChange={(e) => setContactPerson(e.target.value)}
                            placeholder="Ali Yılmaz"
                            className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                        />
                    </label>
                    <label className="block">
                        <span className="overline block mb-2">Görüşme Sonucu</span>
                        <select
                            data-testid="note-outcome"
                            value={outcome}
                            onChange={(e) => setOutcome(e.target.value)}
                            className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue bg-white"
                        >
                            {Object.entries(OUTCOMES).map(([k, v]) => (
                                <option key={k} value={k}>
                                    {v}
                                </option>
                            ))}
                        </select>
                    </label>
                    {outcome === "promised_payment" && (
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="overline block mb-2">Söz Tarihi</span>
                                <input
                                    data-testid="note-promised-date"
                                    type="date"
                                    value={promised}
                                    onChange={(e) => setPromised(e.target.value)}
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue"
                                />
                            </label>
                            <label className="block">
                                <span className="overline block mb-2">Söz Tutarı (₺)</span>
                                <input
                                    data-testid="note-promised-amount"
                                    type="number"
                                    step="0.01"
                                    value={promisedAmount}
                                    onChange={(e) => setPromisedAmount(e.target.value)}
                                    placeholder="0,00"
                                    className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue placeholder:text-neutral-400"
                                />
                            </label>
                        </div>
                    )}
                    <label className="block">
                        <span className="overline block mb-2">Not</span>
                        <textarea
                            data-testid="note-body"
                            required
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            className="w-full border border-black/20 px-3 py-2 text-sm h-28 focus:outline-none focus:border-brand-blue"
                            placeholder="Görüşme özeti, sonraki adımlar…"
                        />
                    </label>
                    <label className="block">
                        <span className="overline block mb-2">Dosya/Fotoğraf (max 10 MB)</span>
                        <input
                            data-testid="note-files"
                            type="file"
                            multiple
                            accept="image/*,.pdf,.xlsx,.xls,.doc,.docx,.csv,.txt"
                            onChange={(e) => setFiles(Array.from(e.target.files || []))}
                            className="w-full text-xs font-mono"
                        />
                        {files.length > 0 && (
                            <div className="text-xs font-mono text-neutral-600 mt-2">{files.length} dosya seçildi</div>
                        )}
                    </label>
                    <button data-testid="note-submit" className="btn-sharp w-full justify-center" disabled={saving}>
                        {saving ? "Kaydediliyor…" : "Notu Kaydet"}
                    </button>
                </form>
            </div>

            <div className="lg:col-span-3">
                <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Görüşme Geçmişi</h2>
                {notes.length === 0 ? (
                    <div className="text-sm text-neutral-500 font-mono">Henüz not yok.</div>
                ) : (
                    <ol className="relative border-l border-black/15 pl-6 space-y-6" data-testid="notes-timeline">
                        {notes.map((n) => (
                            <li key={n.id} className="relative">
                                <span className="absolute -left-[29px] top-1.5 w-2 h-2 bg-brand-blue" />
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-3 flex-wrap mb-2">
                                            <span
                                                className={`inline-block border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                                                    OUTCOME_COLOR[n.call_outcome] || ""
                                                }`}
                                            >
                                                {OUTCOMES[n.call_outcome] || n.call_outcome}
                                            </span>
                                            <span className="text-xs font-mono text-neutral-500">
                                                {formatDateTime(n.created_at)}
                                            </span>
                                            <span className="text-xs font-mono text-neutral-700">
                                                · {n.author_name || "-"}
                                            </span>
                                            {n.contact_person && (
                                                <span className="text-xs font-mono text-neutral-700">
                                                    · Görüşülen: <strong>{n.contact_person}</strong>
                                                </span>
                                            )}
                                            {n.promised_date && (
                                                <span className="text-xs font-mono text-brand-blue">
                                                    Söz: {formatDate(n.promised_date)}
                                                    {n.promised_amount ? ` · ₺${formatMoney(n.promised_amount)}` : ""}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-neutral-800 whitespace-pre-wrap">{n.body}</p>
                                        {(n.attachments || []).length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2" data-testid={`note-attachments-${n.id}`}>
                                                {n.attachments.map((att) => (
                                                    <button
                                                        key={att.id}
                                                        type="button"
                                                        onClick={() => downloadAttachment(n.id, att)}
                                                        className="inline-flex items-center gap-1 border border-black/20 px-2 py-1 text-[11px] font-mono hover:border-brand-blue hover:text-brand-blue"
                                                    >
                                                        📎 {att.filename}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => del(n.id)} className="text-neutral-400 hover:text-brand-red">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
}

function AssignmentSection({ customer, reload }) {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [history, setHistory] = useState([]);
    const [saving, setSaving] = useState(false);

    async function loadHistory() {
        try {
            const r = await api.get(`/customers/${customer.id}/assignment-history`);
            setHistory(r.data);
        } catch {}
    }

    useEffect(() => {
        if (user?.role === "admin") {
            api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "collector")));
            loadHistory();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, customer.id]);

    if (user?.role !== "admin") return null;
    const assigned = new Set(customer.assigned_collector_ids || []);

    async function toggle(uid) {
        setSaving(true);
        const next = new Set(assigned);
        if (next.has(uid)) next.delete(uid);
        else next.add(uid);
        try {
            await api.put(`/customers/${customer.id}`, {
                name: customer.name, code: customer.code, phone: customer.phone,
                email: customer.email, address: customer.address,
                opening_balance: customer.opening_balance, notes: customer.notes,
                assigned_collector_ids: Array.from(next),
            });
            toast.success("Atamalar güncellendi");
            await loadHistory();
            reload();
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="mt-10 border-t border-black/10 pt-8 grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="assignment-section">
            <div>
                <div className="overline mb-2">Yönetici · Erişim Kontrolü</div>
                <h3 className="font-display text-xl font-bold tracking-tight mb-4">Atanmış Tahsilatçılar</h3>
                <p className="text-xs text-neutral-500 mb-4 font-mono">
                    Seçili tahsilatçılar bu müşteriyi görebilir.
                </p>
                <div className="flex flex-wrap gap-2">
                    {users.length === 0 && <div className="text-xs text-neutral-500 font-mono">Kayıtlı tahsilatçı yok.</div>}
                    {users.map((u) => {
                        const active = assigned.has(u.id);
                        return (
                            <button
                                key={u.id}
                                type="button"
                                disabled={saving}
                                data-testid={`assign-toggle-${u.id}`}
                                onClick={() => toggle(u.id)}
                                className={`border px-3 py-1.5 text-xs font-mono transition-colors ${
                                    active
                                        ? "bg-brand-blue text-white border-brand-blue"
                                        : "border-black/20 text-neutral-700 hover:border-brand-blue"
                                }`}
                            >
                                {active ? "✓ " : "+ "}
                                {u.name || u.email}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div>
                <div className="overline mb-2">Denetim İzi</div>
                <h3 className="font-display text-xl font-bold tracking-tight mb-4">Atama Geçmişi</h3>
                {history.length === 0 ? (
                    <div className="text-xs text-neutral-500 font-mono">Henüz atama işlemi yok.</div>
                ) : (
                    <ol className="relative border-l border-black/15 pl-6 space-y-4 max-h-80 overflow-y-auto" data-testid="assignment-history">
                        {history.map((h, i) => (
                            <li key={i} className="relative">
                                <span
                                    className={`absolute -left-[29px] top-1.5 w-2 h-2 ${
                                        h.action === "assigned" ? "bg-brand-green" : "bg-brand-red"
                                    }`}
                                />
                                <div className="text-xs font-mono">
                                    <span
                                        className={`inline-block border px-2 py-0.5 text-[10px] uppercase tracking-widest mr-2 ${
                                            h.action === "assigned"
                                                ? "text-brand-green border-brand-green"
                                                : "text-brand-red border-brand-red"
                                        }`}
                                    >
                                        {h.action === "assigned" ? "Atandı" : "Kaldırıldı"}
                                    </span>
                                    <span className="text-sm text-neutral-800">{h.user_name}</span>
                                </div>
                                <div className="text-[11px] font-mono text-neutral-500 mt-1">
                                    {formatDateTime(h.at)} · {h.actor_name}
                                </div>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
}

function F({ label, value, onChange, type = "text", required, testid, placeholder }) {
    return (
        <label className="block">
            <span className="overline block mb-2">
                {label}
                {required && <span className="text-brand-red"> *</span>}
            </span>
            <input
                data-testid={testid}
                type={type}
                required={required}
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-blue placeholder:text-neutral-400 placeholder:italic"
            />
        </label>
    );
}
